// communication-workflows.js - Tier 1 Educational
export const content = {
  heroStyle: 'gradient',
  tag: 'Systems & Process',

  html: `
<p>The difference between agents who retain and agents who don't often comes down to one thing: systems. Not talent, not personality, not luck—systems. Specifically, communication workflows that ensure the right touchpoint happens at the right time, every time.</p>

<p>Without workflows, retention is random. With workflows, it's predictable. Here's how to build communication systems that drive retention.</p>

<h2>What Is a Communication Workflow?</h2>

<p>A workflow is a defined sequence of communications triggered by an event or schedule. Instead of "I should probably call clients sometime," it's "Day 7 after enrollment, send check-in email. Day 14, reminder to call. Day 30, satisfaction text."</p>

<p>Workflows remove the guesswork and the forgetting. They make retention happen automatically.</p>

<h2>The Essential Workflows</h2>

<h3>1. New Client Onboarding Workflow</h3>

<p>Triggered by: New enrollment</p>

<p>Purpose: Ensure smooth transition, catch early problems, build relationship foundation</p>

<p><strong>Sequence:</strong></p>
<ul>
<li><strong>Day 1:</strong> Welcome email with key coverage information, how to reach you</li>
<li><strong>Day 7:</strong> Check-in text: "How's everything going with your new coverage?"</li>
<li><strong>Day 14:</strong> Call reminder (make an actual call)</li>
<li><strong>Day 30:</strong> Email: Tips for using your benefits</li>
<li><strong>Day 45:</strong> Text: "Any questions so far? I'm here to help."</li>
<li><strong>Day 60:</strong> Call reminder: First bi-monthly check-in</li>
<li><strong>Day 90:</strong> Quarterly review invitation</li>
</ul>

<p>Why it matters: 15.6% of new enrollees leave in year one. Strong onboarding dramatically reduces this.</p>

<h3>2. Quarterly Check-In Workflow</h3>

<p>Triggered by: Calendar (Q1, Q2, Q3, Q4)</p>

<p>Purpose: Maintain relationship, catch life changes, stay top of mind</p>

<p><strong>Sequence:</strong></p>
<ul>
<li><strong>Week 1 of quarter:</strong> Check-in reminder appears in your task list</li>
<li><strong>Attempt 1:</strong> Phone call</li>
<li><strong>If no answer:</strong> Voicemail + follow-up text</li>
<li><strong>2 days later:</strong> Email if still no response</li>
<li><strong>End of month:</strong> Note if unable to reach, flag for extra attention</li>
</ul>

<p>Why it matters: Clients who hear from you quarterly are dramatically less likely to switch than those who only hear from you at AEP.</p>

<h3>3. Birthday Workflow</h3>

<p>Triggered by: Client birthday</p>

<p>Purpose: Personal recognition that builds loyalty</p>

<p><strong>Sequence:</strong></p>
<ul>
<li><strong>On birthday:</strong> Call reminder (call is preferred)</li>
<li><strong>If no answer:</strong> Leave warm voicemail + send birthday text</li>
<li><strong>Within 3 days:</strong> If no call made, send birthday email/card</li>
</ul>

<p>Why it matters: A birthday call takes 2 minutes and creates outsized loyalty. Almost no competitors do this.</p>

<h3>4. Policy Anniversary Workflow</h3>

<p>Triggered by: Enrollment anniversary</p>

<p>Purpose: Acknowledge relationship milestone, review coverage fit</p>

<p><strong>Sequence:</strong></p>
<ul>
<li><strong>1 week before:</strong> Anniversary recognition email</li>
<li><strong>On date:</strong> Call reminder for review conversation</li>
<li><strong>During call:</strong> Review coverage, ask about changes, address concerns</li>
<li><strong>After call:</strong> Update records with any changes discussed</li>
</ul>

<p>Why it matters: Reinforces the relationship and catches issues that might otherwise fester.</p>

<h3>5. Pre-AEP Workflow</h3>

<p>Triggered by: September 1 annually</p>

<p>Purpose: Prepare for enrollment season, defend against poaching</p>

<p><strong>Sequence:</strong></p>
<ul>
<li><strong>September 1-7:</strong> ANOC review—check plan changes affecting your clients</li>
<li><strong>September 8-14:</strong> Outreach to high-risk clients (significant plan changes)</li>
<li><strong>September 15-30:</strong> Outreach to remaining book</li>
<li><strong>October 1-14:</strong> Schedule annual review appointments</li>
<li><strong>October 15+:</strong> Conduct reviews, implement changes</li>
</ul>

<p>Why it matters: By the time AEP starts, your clients should already feel attended to. That's the defense against competitors.</p>

<h3>6. Service Issue Follow-Up Workflow</h3>

<p>Triggered by: Client reports problem</p>

<p>Purpose: Ensure resolution, demonstrate care, prevent churn</p>

<p><strong>Sequence:</strong></p>
<ul>
<li><strong>Immediately:</strong> Log issue, take action to resolve</li>
<li><strong>Within 48 hours:</strong> Follow up with client on status</li>
<li><strong>Upon resolution:</strong> Confirm with client, ensure satisfaction</li>
<li><strong>1 week later:</strong> Final follow-up: "Is everything still working well?"</li>
</ul>

<p>Why it matters: Problems aren't always retention killers. Unresolved problems are. Close the loop.</p>

<h3>7. Plan Change Alert Workflow</h3>

<p>Triggered by: Plan announces changes affecting client</p>

<p>Purpose: Proactive communication before client discovers problem</p>

<p><strong>Sequence:</strong></p>
<ul>
<li><strong>Upon discovery:</strong> Review impact on specific clients</li>
<li><strong>Within 48 hours:</strong> Call affected clients</li>
<li><strong>During call:</strong> Explain change, discuss options, reassure</li>
<li><strong>Follow-up email:</strong> Document conversation, provide resources</li>
</ul>

<p>Why it matters: Clients who learn about changes from you (with solutions) stay. Clients who learn at the pharmacy or from competitors leave.</p>

<h2>Building Your Workflows</h2>

<h3>Step 1: Define the Trigger</h3>
<p>What event or date starts the workflow?</p>
<ul>
<li>Time-based: birthdays, anniversaries, calendar dates</li>
<li>Event-based: enrollment, service issue, plan change</li>
<li>Behavior-based: silence detected, risk score increase</li>
</ul>

<h3>Step 2: Map the Sequence</h3>
<p>What happens and when?</p>
<ul>
<li>Define each step (email, call, text, task)</li>
<li>Set timing between steps</li>
<li>Include fallbacks if primary action doesn't happen</li>
</ul>

<h3>Step 3: Create Content</h3>
<p>What do you say at each step?</p>
<ul>
<li>Write templates that can be personalized</li>
<li>Keep messaging consistent but not robotic</li>
<li>Match tone to situation (service issue = empathetic, birthday = warm)</li>
</ul>

<h3>Step 4: Automate What You Can</h3>
<p>Some steps can be fully automated, others need reminders:</p>
<ul>
<li>Automated: scheduled emails, reminder generation</li>
<li>Prompted: call reminders, decision points</li>
</ul>

<h3>Step 5: Track and Improve</h3>
<p>Monitor workflow performance:</p>
<ul>
<li>Are steps being completed?</li>
<li>Are clients responding?</li>
<li>Is retention improving?</li>
</ul>

<h2>Common Workflow Mistakes</h2>

<h3>Too Many Steps</h3>
<p>Complex workflows don't get followed. Start simple. Add complexity only when basic workflow is working.</p>

<h3>No Accountability</h3>
<p>Workflows without tracking become suggestions. Track completion. Know when steps are missed.</p>

<h3>One-Size-Fits-All</h3>
<p>Different client segments need different approaches. High-value clients might get more touchpoints. New enrollees need more attention. Customize where it matters.</p>

<h3>Ignoring Results</h3>
<p>If a workflow isn't improving retention, change it. Workflows are tools, not religious doctrine.</p>

<h2>Getting Started</h2>

<p>Don't try to implement everything at once. Start with:</p>
<ol>
<li>Birthday workflow (simple, high impact)</li>
<li>Quarterly check-in workflow (covers the basics)</li>
<li>New enrollee onboarding (protects vulnerable new clients)</li>
</ol>

<p>Add more workflows as these become routine. The goal isn't perfection—it's progress from random to systematic.</p>
  `,

  faqs: [
    {
      question: "What's the most important workflow to implement first?",
      answer: "Start with birthday calls (simple, high impact), quarterly check-ins (covers basic retention), and new enrollee onboarding (protects your most vulnerable clients). These three cover the essentials. Add more as these become routine."
    },
    {
      question: "How many steps should a workflow have?",
      answer: "Keep it simple—5-7 steps maximum for most workflows. Complex workflows don't get followed. Start basic and add complexity only when the simple version is working consistently."
    },
    {
      question: "Should I automate everything in my workflows?",
      answer: "No. Automate scheduling, reminders, and routine communications. Keep personal touchpoints (actual phone calls, complex conversations) as prompted tasks. The goal is systematic, not robotic."
    },
    {
      question: "How do I know if my workflows are working?",
      answer: "Track completion rates (are steps being done?), response rates (are clients engaging?), and retention rates (are clients staying?). If retention isn't improving, adjust the workflow."
    },
    {
      question: "What tools do I need for communication workflows?",
      answer: "At minimum: a CRM that tracks contacts and schedules reminders, email/text capability, and calendar integration. Advanced: automation platform that executes steps automatically. You can start simple and upgrade over time."
    }
  ],

  sources: [
    {
      title: "Client Retention Best Practices",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "KFF",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Communication workflows only work if they actually happen. Pro IRP automates the scheduling, reminders, and tracking so your workflows run reliably—not when you remember, but every time.`
};

export default content;
